import { Layers, TrendingUp, TrendingDown, Activity } from "lucide-react";
import { computeCrowding } from "@/lib/signals";
import { SkeletonBar } from "./Skeleton";

// Detects a clustered USD-directional crowding regime from COT Index across FX.
export default function CrowdingRegime({ rows = [] }) {
  // Cold load — show a shimmer instead of a blank gap until the snapshot lands.
  if (!rows || rows.length === 0) {
    return (
      <div className="border border-zinc-800 bg-[#09090B] px-3 py-2 flex items-center gap-3" data-testid="crowding-regime-skeleton">
        <SkeletonBar className="h-3 w-32" />
        <SkeletonBar className="h-3 w-40" />
        <SkeletonBar className="h-3 w-24 ml-auto" />
      </div>
    );
  }
  const c = computeCrowding(rows);
  if (!c) return null;

  const { dominant, votes, total, names, clustered, strong } = c;

  const dir = dominant === "long" ? "USD LONG" : "USD SHORT";
  const Icon = dominant === "long" ? TrendingUp : TrendingDown;
  const implication =
    dominant === "long"
      ? "Positioning stretched long the dollar — contrarian USD downside / reversal risk"
      : "Positioning stretched short the dollar — contrarian USD upside / reversal risk";

  const tone = strong
    ? "border-red-500/50 bg-red-950/[0.14]"
    : clustered
    ? "border-amber-500/50 bg-amber-950/[0.14]"
    : "border-zinc-800 bg-[#09090B]";
  const accent = strong ? "text-neg" : clustered ? "text-amber-term" : "text-zinc-400";

  return (
    <div className={`border ${tone} px-3 py-2 flex items-center gap-3 flex-wrap`} data-testid="crowding-regime-banner">
      <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-500">
        <Layers size={12} strokeWidth={1.5} /> Crowding Regime
      </span>
      <span className={`inline-flex items-center gap-1.5 text-sm font-semibold ${accent}`} data-testid="crowding-label">
        <Icon size={15} strokeWidth={2} />
        {clustered ? `CROWDED ${dir}` : `MIXED · NO CLUSTER`}
      </span>
      <span className="text-[11px] tabular-nums text-zinc-400">
        <span className="text-zinc-600 uppercase tracking-widest text-[11px] mr-1">consensus</span>
        {votes}/{total} FX books
      </span>
      {names.length > 0 && (
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 hidden md:inline" data-testid="crowding-members">
          {names.join(" · ")}
        </span>
      )}
      <span className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest ml-auto ${accent}`}>
        <Activity size={11} strokeWidth={1.5} /> {implication}
      </span>
    </div>
  );
}
